import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Link } from "react-router-dom";
import { ClipboardSignature } from "lucide-react";
import { api } from "@/lib/api";
import TraegerFormPanel from "@/components/TraegerFormPanel";
import { TraegerStatusBadge } from "@/pages/TraegerbestaetigungPage";

export function TraegerStatusCard({ kind, personId, onlyMissing = false, inline = false, onChanged }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const loadSt = () => api.get(`/traeger-forms/status/${kind}/${personId}`).then((r) => setSt(r.data)).catch(() => setSt(null));
  useEffect(() => { loadSt(); }, [kind, personId]); // eslint-disable-line
  if (!st || (onlyMissing && ["printed", "sent"].includes(st.status.code))) return null;
  const status = st.status;
  const missing = [!st.upload && (kind === "client" ? "Noch keine Trägerbestätigung hochgeladen – bitte die des Kunden hochladen." : "Noch kein Bildungsgutschein hochgeladen – bitte den des Kunden hochladen."), !st.contract && "Vertrag fehlt.",
    st.deviations?.length && "Daten weichen vom unterschriebenen Vertrag ab."].filter(Boolean).join(" ");
  const docName = kind === "client" ? "Trägerbestätigung" : "Bildungsgutschein";
  return (
    <div className="border border-slate-200 bg-white" data-testid="traeger-status-card">
    <div className="p-4 flex flex-wrap items-center gap-3">
      <ClipboardSignature size={16} className="text-slate-500" />
      <span className="text-sm font-medium text-slate-900">{kind === "client" ? "Trägerbestätigung" : "Bildungsgutschein"}</span> <InfoTip k="traeger" />
      <TraegerStatusBadge status={status} testId="traeger-status-card-badge" />
      {missing && <span className="text-xs text-red-700" data-testid="traeger-status-missing">{missing}</span>}
      {st.upload && <span className="text-xs text-slate-500">hochgeladen am {new Date(st.upload.created_at).toLocaleDateString("de-DE")}{st.upload.created_by_name ? ` von ${st.upload.created_by_name}` : ""}</span>}
      {inline ? (
        <button onClick={() => setOpen((o) => !o)} className="ml-auto text-xs border border-slate-300 px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors" data-testid="traeger-inline-toggle">
          {open ? "Schließen" : st.upload ? `${docName} bearbeiten / senden` : `${docName} hochladen & ausfüllen`}
        </button>
      ) : (
        <Link to={`/traegerbestaetigung?kind=${kind}&id=${personId}`} className="ml-auto text-xs border border-slate-300 px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors" data-testid="traeger-status-open">{st.upload ? "Zur Trägerbestätigung" : `${docName} hochladen`}</Link>
      )}
    </div>
    {inline && open && (
      <div className="border-t border-slate-200 p-4 bg-slate-50/50" data-testid="traeger-inline-panel">
        <TraegerFormPanel kind={kind} personId={personId} onChanged={() => { loadSt(); onChanged?.(); }} />
      </div>
    )}
    </div>
  );
}

const STATUS_ICON = { missing: "text-red-500", failed: "text-red-500", progress: "text-amber-500", printed: "text-sky-500", sent: "text-emerald-600" };

export const isTraegerMissing = (info) => !!info && !["printed", "sent"].includes(info.form_status.code);

export function useTraegerStatusMap(kind, enabled = true) {
  const [map, setMap] = useState({});
  useEffect(() => {
    if (!enabled) return;
    api.get(`/traeger-forms/picker/${kind}`).then((r) => setMap(Object.fromEntries(r.data.map((p) => [p.id, p])))).catch(() => {});
  }, [kind, enabled]);
  return map;
}

export function TraegerDot({ kind, id, info }) {
  if (!info) return null;
  const doc = kind === "client" ? "Trägerbestätigung" : "Bildungsgutschein";
  const tip = `${doc}: ${info.form_status.label}${info.has_contract ? "" : " · Vertrag fehlt"}`;
  return (
    <Link to={`/traegerbestaetigung?kind=${kind}&id=${id}`} onClick={(e) => e.stopPropagation()} title={tip} aria-label={tip}
      className="inline-flex items-center justify-center w-5 h-5 ml-1 align-middle shrink-0 hover:scale-125 transition-transform" data-testid={`traeger-dot-${id}`}>
      <ClipboardSignature size={14} strokeWidth={2} className={STATUS_ICON[info.form_status.code] || "text-slate-300"} data-status={info.form_status.code} />
    </Link>
  );
}
